import { AnimatedContentLoader, Progress } from '@components'
import { useTheme } from '@hooks'
import { useTrialLimits } from '@query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const getUsageProgressColor = (
  percent: number,
  colors: { success: string; warning: string; error: string }
) => {
  if (percent < 70) return colors.success
  if (percent < 90) return colors.warning
  return colors.error
}

const getTrialPeriodPercent = (startedAt: string | null, endsAt: string | null) => {
  if (!startedAt || !endsAt) return null

  const start = new Date(startedAt).setHours(0, 0, 0, 0)
  const end = new Date(endsAt).setHours(23, 59, 59, 999)
  const now = Date.now()

  if (end <= start) return 100

  return Math.max(Math.min(Math.floor(((now - start) / (end - start)) * 100), 100), 0)
}

const getUsagePercent = (used: number, limit: number) =>
  limit > 0 ? Math.min((used / limit) * 100, 100) : 0

interface UsageProgressItemProps {
  label: string
  usageLabel: string
  percent: number
  progressColor: string
}

const UsageProgressItem: React.FC<UsageProgressItemProps> = ({
  label,
  usageLabel,
  percent,
  progressColor
}) => (
  <ProgressItem>
    <ProgressHeader>
      <ProgressLabel>{label}</ProgressLabel>
      <ProgressUsage>{usageLabel}</ProgressUsage>
    </ProgressHeader>
    <Progress initialAnimation percent={percent} progressColor={progressColor} />
  </ProgressItem>
)

export const TrialUsageSection: React.FC = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { data: trialLimits, isLoading } = useTrialLimits()
  const progressColors = {
    success: theme.colors.success,
    warning: theme.colors.warning,
    error: theme.colors.error
  }

  const documentUsage = trialLimits?.document_usage
  const hasTrialDocumentLimit = documentUsage?.limit != null
  const trialLimit = documentUsage?.limit ?? 0
  const trialUsed = documentUsage?.used ?? 0
  const trialRemaining = Math.max(documentUsage?.remaining ?? trialLimit - trialUsed, 0)
  const trialDocumentPercent = getUsagePercent(trialUsed, trialLimit)

  const paygInvoiceUsage = trialLimits?.payg_invoice_usage
  const hasPaygInvoiceLimit = Boolean(
    paygInvoiceUsage?.is_enabled && paygInvoiceUsage.limit != null
  )
  const paygInvoiceLimit = paygInvoiceUsage?.limit ?? 0
  const paygInvoiceUsed = paygInvoiceUsage?.used ?? 0
  const paygInvoiceRemaining = Math.max(
    paygInvoiceUsage?.remaining ?? paygInvoiceLimit - paygInvoiceUsed,
    0
  )
  const paygInvoicePercent = getUsagePercent(paygInvoiceUsed, paygInvoiceLimit)

  const trialDaysRemaining = trialLimits?.trial_days_remaining
  const trialPeriodPercent = getTrialPeriodPercent(
    trialLimits?.trial_started_at ?? null,
    trialLimits?.trial_ends_at ?? null
  )
  const hasTrialPeriod = trialPeriodPercent != null && trialDaysRemaining != null

  return (
    <AnimatedContentLoader isLoading={isLoading}>
      <ProgressList>
        {hasTrialPeriod && (
          <UsageProgressItem
            label={t('settings.billing.paygUsage.trial.trialPeriod')}
            usageLabel={t('settings.billing.paygUsage.trial.trialPeriodUsage', {
              daysRemaining: trialDaysRemaining
            })}
            percent={trialPeriodPercent}
            progressColor={getUsageProgressColor(trialPeriodPercent, progressColors)}
          />
        )}
        {hasTrialDocumentLimit && (
          <UsageProgressItem
            label={t('settings.billing.paygUsage.trial.documents')}
            usageLabel={t('settings.billing.paygUsage.trial.documentsUsage', {
              used: trialUsed,
              limit: trialLimit,
              remaining: trialRemaining
            })}
            percent={trialDocumentPercent}
            progressColor={getUsageProgressColor(trialDocumentPercent, progressColors)}
          />
        )}
        {hasPaygInvoiceLimit && (
          <UsageProgressItem
            label={t('settings.billing.paygUsage.trial.invoices')}
            usageLabel={t('settings.billing.paygUsage.trial.invoicesUsage', {
              used: paygInvoiceUsed,
              limit: paygInvoiceLimit,
              remaining: paygInvoiceRemaining
            })}
            percent={paygInvoicePercent}
            progressColor={getUsageProgressColor(paygInvoicePercent, progressColors)}
          />
        )}
      </ProgressList>
    </AnimatedContentLoader>
  )
}

const ProgressList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const ProgressItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const ProgressHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const ProgressLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const ProgressUsage = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  text-align: right;
  white-space: nowrap;
`
